import * as yup from 'yup';
import { MixCodesOriginFieldValidation } from '../MixCodes/MixCodesCodeOriginValidation';

interface TestContextExtended {
  [key: string]: any;
}

const alphaNumericRegex = /^[a-zA-Z0-9]+$/;

function ProofOfPurchaseValidationSchema(flowName: string) {
  return yup
    .object({
      tempParams: yup.object({
        pincodeOriginValitidy: yup.boolean(),
        codeType: yup
          .array()
          .of(yup.string())
          .default(() => []),
        mixCodesPrizeMapping: yup.boolean().default(false),
      }),
      params: yup.object({
        campaignIds: yup
          .array()
          .of(yup.string())
          .default(() => []),
        lotIds: yup
          .array()
          .of(yup.string())
          .default(() => []),
        mixCodesPrizeMapping: yup.object({
          mapping: yup
            .array()
            .of(
              yup.object({
                prizes: yup
                  .array()
                  .of(yup.string())
                  .default(() => []),
              })
            )
            .default(() => [{ prizes: [] }]),
        }),
      }),
      secrets: yup.object().shape({
        mixCodesParameters: yup
          .mixed()
          .test('mixcodesTest', 'If functionality is applied, mixcodes details are required', (value, testContext) => {
            const { from } = testContext as yup.TestContext & TestContextExtended;
            const proofType = from[1].value.proofType;
            if (proofType !== 'mixCodes') {
              return true;
            }
            let result = true;
            value &&
              value.forEach((element: { programId: string; secret: string; uri: string }) => {
                if (element.programId === undefined || element.secret === undefined || element.uri === undefined) {
                  result = false;
                }
              });
            return result;
          })
          .test(
            'programIdTest',
            'Program Id must contain alphanumeric characters with a length of 26 or numeric of 6-8 ',
            (value, testContext) => {
              const { from } = testContext as yup.TestContext & TestContextExtended;
              const proofType = from[1].value.proofType;
              if (proofType !== 'mixCodes') {
                return true;
              }
              let result = true;
              value &&
                value.forEach((element: { programId: string; secret: string; uri: string }) => {
                  if (
                    element.programId !== undefined &&
                    !((alphaNumericRegex.test(element.programId) && element.programId.length === 26) || /^\d{6,8}$/.test(element.programId))
                  ) {
                    result = false;
                  }
                });
              return result;
            }
          ),
      }),
      pluginParams: yup.object().shape({
        emailTemplateId: yup.string().test('emailTemplateId required test', 'emailTemplateId is required', (value, testContext) => {
          const { from } = testContext as yup.TestContext & TestContextExtended;
          let result = true;
          if (from[1].value?.proofType !== 'receiptScanner') {
            return true;
          }
          if (!value) {
            result = false;
          }
          return result;
        }),
        rejectedEmailTemplateId: yup
          .string()
          .test('rejectedEmailTemplateId required test', 'rejectedEmailTemplateId is required', (value, testContext) => {
            const { from } = testContext as yup.TestContext & TestContextExtended;
            let result = true;
            if (from[1].value?.proofType !== 'receiptScanner') {
              return true;
            }
            if (!value) {
              result = false;
            }
            return result;
          }),
        url: yup.object().test('url required test', 'url is required', function (value, testContext) {
          const { from } = testContext as yup.TestContext & TestContextExtended;
          const isEnabled = from[2]?.value?.proofType === 'receiptScanner';

          if (isEnabled) {
            // Check if the URL object is empty or has undefined values
            if (
              !value ||
              (typeof value === 'object' && Object.keys(value).length === 0) ||
              Object.values(value).some((v) => v === undefined)
            ) {
              return this.createError({
                message: 'URL object cannot be empty',
                path: 'pluginParams.url',
              });
            }

            // Check each URL for validity
            const urlValidationResults = Object.entries(value)
              .map(([key, url]) => {
                if (url && typeof url === 'string') {
                  if (!/^https?:\/\/[^ ]+$/i.test(url)) {
                    return this.createError({
                      message: `Invalid URL for key ${key}`,
                      path: 'pluginParams.url',
                    });
                  }
                } else {
                  return this.createError({
                    message: `URL for key ${key} is required and cannot be empty`,
                    path: 'pluginParams.url',
                  });
                }
                return null;
              })
              .filter((error) => error);

            if (urlValidationResults.length > 0) {
              return urlValidationResults[0];
            }
          }

          return true;
        }),
      }),
      flow: yup.object().shape({
        [flowName]: yup.object().shape({
          pluginRoutes: yup.object().shape({
            processReceipts: yup.object().shape({
              externalReqParams: yup.object().shape({
                scannerConfigInstanceId: yup
                  .string()
                  .test('scannerConfigInstanceId required test', 'scannerConfigInstanceId is required', (value, testContext) => {
                    const { from } = testContext as yup.TestContext & TestContextExtended;
                    let result = true;
                    if (from[5].value?.proofType !== 'receiptScanner') {
                      return true;
                    }
                    if (!value) {
                      result = false;
                    }
                    return result;
                  }),
                timeoutInMillis: yup.number().test('timeoutInMillis required test', 'timeoutInMillis is required', (value, testContext) => {
                  let result = true;
                  if (!value) {
                    result = false;
                  }
                  return result;
                }),
              }),
            }),
          }),
        }),
      }),
    })
    .test('validate-campaign-lot', function (values) {
      const valResult = MixCodesOriginFieldValidation(values);

      if (valResult.isValid) {
        return true;
      }

      if ('message' in valResult) {
        return this.createError({
          path: valResult.path,
          message: valResult.message,
        });
      }
    })
    .test(
      'prizes-required-if-enabled',
      'At least one prize ID is required for each mapping when prize mapping is enabled',
      function (values, testContext) {
        const { from } = testContext as yup.TestContext & TestContextExtended;
        const proofType = from[0].value.proofType;
        if (proofType !== 'mixCodes') {
          return true;
        }
        if (values?.tempParams.mixCodesPrizeMapping) {
          const mapping = values.params?.mixCodesPrizeMapping?.mapping;
          for (const [index, mapItem] of mapping.entries()) {
            if (!Array.isArray(mapItem.prizes) || mapItem.prizes.length === 0) {
              return this.createError({
                path: `params.mixCodesPrizeMapping.mapping[${index}].prizes`,
                message: 'At least one prize ID is required when prize mapping is enabled',
              });
            }
          }
        }
        return true;
      }
    )
    .test('lot-required-if-enabled', 'Enter Lot Id is required when prize mapping is enabled', function (values, testContext) {
      const { from } = testContext as yup.TestContext & TestContextExtended;
      const proofType = from[0].value.proofType;
      if (proofType !== 'mixCodes') {
        return true;
      }
      if (values?.tempParams.mixCodesPrizeMapping && values.tempParams.codeType?.[0] === 'Pincodes') {
        const mapping = values.params?.mixCodesPrizeMapping?.mapping || [];
        for (let i = 0; i < mapping.length; i++) {
          const lot = mapping[i].pincodes;
          if (typeof lot !== 'string' || lot.trim() === '') {
            return this.createError({
              path: `params.mixCodesPrizeMapping.mapping[${i}].pincodes`,
              message: 'Enter Lot ID is required when prize mapping is enabled',
            });
          }
        }
      }
      return true;
    });
}

export { ProofOfPurchaseValidationSchema };
